import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Renderer2,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { BrokenImageDirective } from '@rma/generic/ui/broken-image';
import { ProfileBannerLogo } from '@rma/generic/util/pipes/image-optimiser';
import { isWhite } from '@rma/generic/util/pipes/is-white';

@Component({
  selector: 'rma-branded-header',
  imports: [BrokenImageDirective, NgOptimizedImage],
  templateUrl: './branded-header.component.html',
  host: {
    '[style.display]': 'display()',
    '[style.background-color]': 'background()',
    '[class]': 'hostClass()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandedHeaderComponent {
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);

  public readonly background = input<string>();
  public readonly logo = input<string>();
  public readonly logoPosition = input<'start' | 'center' | 'end'>('center');
  public readonly logoPriority = input(false, { transform: booleanAttribute });
  public readonly imageOptimisation = input<string>(ProfileBannerLogo);

  public readonly display = computed(() => (this.logo() ? 'flex' : 'none'));
  public readonly imageHeight = computed(() => (this.logoPosition() === 'center' ? 'max-h-full' : 'max-h-6'));
  public readonly hostClass = signal('items-center justify-center py-2 px-4 overflow-hidden w-full ');

  constructor() {
    effect(() => untracked(() => this.requiresUnderline(this.background() || '#fff')));

    effect(() => {
      const logoPosition = this.logoPosition();

      untracked(() => {
        this.hostClass.update((hostClass) => `${hostClass.replace('justify-center', '')} justify-${logoPosition}`);
      });
    });
  }

  private requiresUnderline(color: string) {
    const underline = 'border-b border-steel-20';
    const required = isWhite(color);

    this.hostClass.update((hostClass) => hostClass.replace(underline, ''));
    this.renderer.setStyle(this.elementRef.nativeElement, 'border-bottom-left-radius', 'initial');
    this.renderer.setStyle(this.elementRef.nativeElement, 'border-bottom-right-radius', 'initial');

    if (required) {
      this.hostClass.update((hostClass) => `${underline} ${hostClass}`);

      this.renderer.setStyle(this.elementRef.nativeElement, 'border-bottom-left-radius', 0, 1);
      this.renderer.setStyle(this.elementRef.nativeElement, 'border-bottom-right-radius', 0, 1);
    }
  }
}
